/**
 * ResourcesSidebar — left panel with Beats / Outline / Characters / Notes.
 *
 * Two modes:
 *   Tabs (default) — one section at a time, classic tabs
 *   Stack — all visible sections stacked vertically and scrollable. Each
 *           section can be minimized, removed, or drag-reordered. The chosen
 *           order is persisted (localStorage) and also drives the tab order.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { buildOutline } from "@/lib/screenplayDetector";
import { toast } from "sonner";
import { Reorder, useDragControls } from "framer-motion";
import { Plus, Trash2, Upload, ChevronLeft, ChevronRight, ChevronDown, ChevronsDownUp, ChevronsUpDown, Pin, Users, BookOpen, StickyNote, ListOrdered, X, GripVertical, Bold, Italic, Heading3, List, RefreshCw, File as FileIcon, FileText as FileTextIcon, FileAudio, FileVideo } from "lucide-react";
import { CharactersPanel } from "@/components/editor/CharactersPanel";
import { useLightbox } from "@/components/ImageLightbox";

const TABS = [
  { id: "beats", label: "Beats", icon: BookOpen },
  { id: "outline", label: "Outline", icon: ListOrdered },
  { id: "characters", label: "Characters", icon: Users },
  { id: "notes", label: "Notes", icon: StickyNote },
];
const TABS_BY_ID = Object.fromEntries(TABS.map((t) => [t.id, t]));
const DEFAULT_ORDER = ["beats", "outline", "characters", "notes"];

const SIDEBAR_MIN_W = 240;
const SIDEBAR_MAX_W = 640;
const clampSidebarWidth = (w) => Math.max(SIDEBAR_MIN_W, Math.min(SIDEBAR_MAX_W, Math.round(w)));

const STACK_CONFIG_KEY = "screenplay_sidebar_stack_config";
const TAB_ORDER_KEY = "screenplay_sidebar_tab_order";
const STACK_DEFAULT = {
  beats: { visible: true, minimized: false },
  outline: { visible: true, minimized: false },
  characters: { visible: true, minimized: false },
  notes: { visible: true, minimized: false },
};

function loadStackConfig() {
  try {
    const raw = localStorage.getItem(STACK_CONFIG_KEY);
    if (!raw) return STACK_DEFAULT;
    return { ...STACK_DEFAULT, ...JSON.parse(raw) };
  } catch {
    return STACK_DEFAULT;
  }
}
function saveStackConfig(cfg) {
  try { localStorage.setItem(STACK_CONFIG_KEY, JSON.stringify(cfg)); } catch { /* ignore */ }
}

function loadOrder() {
  try {
    const raw = localStorage.getItem(TAB_ORDER_KEY);
    const ids = TABS.map((t) => t.id);
    if (!raw) return DEFAULT_ORDER;
    const parsed = JSON.parse(raw).filter((id) => ids.includes(id));
    for (const id of ids) if (!parsed.includes(id)) parsed.push(id);
    return parsed.length ? parsed : DEFAULT_ORDER;
  } catch {
    return DEFAULT_ORDER;
  }
}
function saveOrder(order) {
  try { localStorage.setItem(TAB_ORDER_KEY, JSON.stringify(order)); } catch { /* ignore */ }
}

function normalizePrefOrder(raw) {
  if (!Array.isArray(raw)) return null;
  const ids = TABS.map((t) => t.id);
  const parsed = raw.filter((id) => ids.includes(id));
  for (const id of ids) if (!parsed.includes(id)) parsed.push(id);
  return parsed.length ? parsed : null;
}

export function ResourcesSidebar({ scriptId, elements, collapsed, onToggle, jumpToScene, charactersRefreshKey, hideChrome = false, jumpToElement, onLocateCharacter, canGenerate = true, resizable = false, initialWidth = 300, onWidthCommit }) {
  const { user } = useAuth();
  const serverPrefs = user?.resources_prefs || null;
  const [order, setOrder] = useState(() => normalizePrefOrder(serverPrefs?.order) || loadOrder());
  const [activeTab, setActiveTab] = useState(() => {
    const o = normalizePrefOrder(serverPrefs?.order) || loadOrder();
    return serverPrefs?.activeTab && TABS_BY_ID[serverPrefs.activeTab] ? serverPrefs.activeTab : (o[0] || "beats");
  });
  const [stackMode, setStackMode] = useState(() => !!serverPrefs?.stackMode);
  const [stackCfg, setStackCfg] = useState(() => (serverPrefs?.stackCfg ? { ...STACK_DEFAULT, ...serverPrefs.stackCfg } : loadStackConfig()));
  const [addOpen, setAddOpen] = useState(false);

  // Remember the layout (tab, stack setup, order) per user — synced across devices.
  const prefsSnapshotRef = useRef(null);
  useEffect(() => {
    const prefs = { activeTab, stackMode, stackCfg, order };
    const snap = JSON.stringify(prefs);
    if (prefsSnapshotRef.current === null) { prefsSnapshotRef.current = snap; return; }
    if (snap === prefsSnapshotRef.current) return undefined;
    prefsSnapshotRef.current = snap;
    const t = setTimeout(() => { api.put("/auth/me", { resources_prefs: prefs }).catch(() => {}); }, 800);
    return () => clearTimeout(t);
  }, [activeTab, stackMode, stackCfg, order]);

  const [width, setWidth] = useState(() => clampSidebarWidth(initialWidth || 300));
  const [dragging, setDragging] = useState(false);
  const dragWidthRef = useRef(null);
  useEffect(() => { if (!dragging) setWidth(clampSidebarWidth(initialWidth || 300)); }, [initialWidth, dragging]);

  const startResize = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = width;
    setDragging(true);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    const onMove = (ev) => {
      const w = clampSidebarWidth(startW + (ev.clientX - startX));
      dragWidthRef.current = w;
      setWidth(w);
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      setDragging(false);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      if (dragWidthRef.current != null && onWidthCommit) onWidthCommit(dragWidthRef.current);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const orderedTabs = order.map((id) => TABS_BY_ID[id]).filter(Boolean);

  const updateStack = (sectionId, patch) => {
    setStackCfg((prev) => {
      const next = { ...prev, [sectionId]: { ...prev[sectionId], ...patch } };
      saveStackConfig(next);
      return next;
    });
  };

  const handleReorder = (newVisibleTabs) => {
    const visibleIds = newVisibleTabs.map((t) => t.id);
    const nextOrder = [...visibleIds, ...order.filter((id) => !visibleIds.includes(id))];
    setOrder(nextOrder);
    saveOrder(nextOrder);
  };

  const renderPanel = (id) => {
    if (id === "beats") return <BeatsPanel scriptId={scriptId} />;
    if (id === "outline") return <OutlinePanel elements={elements} jumpToScene={jumpToScene} scriptId={scriptId} canGenerate={canGenerate} />;
    if (id === "characters") return <CharactersPanel scriptId={scriptId} refreshKey={charactersRefreshKey} elements={elements} jumpToElement={jumpToElement} onLocateCharacter={onLocateCharacter} />;
    if (id === "notes") return <NotesPanel scriptId={scriptId} />;
    return null;
  };

  if (collapsed) {
    return (
      <aside className="w-12 shrink-0 bg-[var(--sidebar-bg)] border-r border-[var(--line)] flex flex-col items-center py-4 gap-2" data-testid="sidebar-collapsed">
        <button
          onClick={onToggle}
          className="p-2 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-secondary)]"
          data-testid="sidebar-expand-btn"
          title="Expand resources"
        >
          <ChevronRight size={18} strokeWidth={1.5} />
        </button>
        {orderedTabs.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => { setActiveTab(t.id); onToggle(); }}
              className="p-2 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-secondary)]"
              data-testid={`sidebar-tab-collapsed-${t.id}`}
              title={t.label}
            >
              <Icon size={18} strokeWidth={1.5} />
            </button>
          );
        })}
      </aside>
    );
  }

  const visibleOrderedTabs = orderedTabs.filter((t) => stackCfg[t.id]?.visible);
  const hiddenStackSections = orderedTabs.filter((t) => !stackCfg[t.id]?.visible);

  return (
    <aside
      className={`${hideChrome ? "w-full" : "relative"} shrink-0 bg-[var(--sidebar-bg)] ${hideChrome ? "" : "border-r border-[var(--line)]"} flex flex-col h-full`}
      style={hideChrome ? undefined : { width }}
      data-testid="sidebar"
    >
      {!hideChrome && (
        <div className="flex items-center justify-between px-4 h-12 border-b border-[var(--line)] shrink-0">
          <span className="font-display text-base text-[var(--ink-primary)]">Resources</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setStackMode((s) => !s)}
              className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${stackMode ? "bg-[var(--warm-accent)] text-[var(--ink-primary)]" : "text-[var(--ink-muted)] hover:bg-[var(--warm-accent)]"}`}
              data-testid="sidebar-stack-toggle"
              title="Stack all sections (drag to reorder)"
            >
              {stackMode ? "Tabs" : "Stack"}
            </button>
            <button
              onClick={onToggle}
              className="p-1.5 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-secondary)]"
              data-testid="sidebar-collapse-btn"
              title="Collapse"
            >
              <ChevronLeft size={16} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      )}

      {!stackMode && (
        <div className="flex border-b border-[var(--line)] text-[11px] tracking-wider uppercase shrink-0">
          {orderedTabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex-1 py-2.5 transition-colors ${activeTab === t.id ? "text-[var(--brand)] border-b-2 border-[var(--brand)] -mb-px font-medium" : "text-[var(--ink-muted)] hover:text-[var(--ink-secondary)]"}`}
              data-testid={`sidebar-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-y-auto" data-testid="sidebar-content">
        {!stackMode ? (
          renderPanel(activeTab)
        ) : (
          <div>
            {hiddenStackSections.length > 0 && (
              <div className="p-3 relative border-b border-[var(--line)]">
                <button
                  onClick={() => setAddOpen((o) => !o)}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs uppercase tracking-wider rounded border border-dashed border-[var(--line-strong)] text-[var(--ink-secondary)] hover:bg-[var(--warm-accent)]"
                  data-testid="stack-add-section-btn"
                >
                  <Plus size={14} strokeWidth={1.5} /> Add a section
                </button>
                {addOpen && (
                  <div className="absolute left-3 right-3 top-full mt-1 bg-white border border-[var(--line)] rounded shadow-lg z-40" data-testid="stack-add-menu">
                    {hiddenStackSections.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => { updateStack(t.id, { visible: true, minimized: false }); setAddOpen(false); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--warm-accent)]"
                        data-testid={`stack-add-${t.id}`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <Reorder.Group axis="y" values={visibleOrderedTabs} onReorder={handleReorder} className="divide-y divide-[var(--line)] list-none m-0 p-0" as="div">
              {visibleOrderedTabs.map((t) => (
                <DraggableStackSection
                  key={t.id}
                  tab={t}
                  minimized={!!stackCfg[t.id]?.minimized}
                  onToggleMinimize={() => updateStack(t.id, { minimized: !stackCfg[t.id]?.minimized })}
                  onRemove={() => updateStack(t.id, { visible: false, minimized: false })}
                >
                  {renderPanel(t.id)}
                </DraggableStackSection>
              ))}
            </Reorder.Group>
          </div>
        )}
      </div>
      {resizable && !hideChrome && (
        <div
          onPointerDown={startResize}
          className={`absolute top-0 right-0 z-20 h-full w-1.5 cursor-col-resize transition-colors ${dragging ? "bg-[var(--brand)]" : "hover:bg-[var(--brand-soft)]"}`}
          data-testid="sidebar-resize-handle"
          title="Drag to resize"
        />
      )}
    </aside>
  );
}

export function DraggableStackSection({ tab, minimized, onToggleMinimize, onRemove, children }) {
  const controls = useDragControls();
  return (
    <Reorder.Item
      value={tab}
      dragListener={false}
      dragControls={controls}
      as="section"
      className="bg-[var(--sidebar-bg)]"
      data-testid={`stack-section-${tab.id}`}
    >
      <header className="flex items-center gap-1 px-2 py-2 bg-[var(--sidebar-bg)] sticky top-0 z-10">
        <button
          onPointerDown={(e) => controls.start(e)}
          className="p-1 text-[var(--ink-muted)] hover:text-[var(--ink-primary)] cursor-grab active:cursor-grabbing touch-none shrink-0"
          data-testid={`stack-drag-${tab.id}`}
          title="Drag to reorder"
        >
          <GripVertical size={14} />
        </button>
        <button
          onClick={onToggleMinimize}
          className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[var(--ink-muted)] hover:text-[var(--ink-primary)] flex-1 text-left"
          data-testid={`stack-toggle-${tab.id}`}
        >
          {minimized ? <ChevronRight size={12} strokeWidth={2} /> : <ChevronDown size={12} strokeWidth={2} />}
          {tab.label}
        </button>
        <button
          onClick={onRemove}
          className="p-1 text-[var(--ink-muted)] hover:text-[var(--err)] opacity-60 hover:opacity-100"
          data-testid={`stack-remove-${tab.id}`}
          title="Remove this section"
        >
          <X size={12} strokeWidth={2} />
        </button>
      </header>
      <div className={minimized ? "hidden" : ""}>{children}</div>
    </Reorder.Item>
  );
}

// ============================================================================
// Outline
// ============================================================================
function OutlinePanel({ elements, jumpToScene, scriptId, canGenerate = true }) {
  const outline = buildOutline(elements || []);
  const [summaries, setSummaries] = useState({});
  const [busy, setBusy] = useState({});
  const [bulk, setBulk] = useState(false);
  const reloadTimer = useRef(null);

  const reload = useCallback(async () => {
    if (!scriptId) return;
    try {
      const { data } = await api.get(`/scripts/${scriptId}/outline/summaries`);
      const map = {};
      (data.summaries || []).forEach((s) => { map[s.scene_id] = s; });
      setSummaries(map);
    } catch { /* ignore */ }
  }, [scriptId]);

  useEffect(() => { reload(); }, [reload]);

  // Re-fetch once edits settle so the "Outdated" flags stay current.
  useEffect(() => {
    if (!scriptId) return undefined;
    if (reloadTimer.current) clearTimeout(reloadTimer.current);
    reloadTimer.current = setTimeout(reload, 1500);
    return () => reloadTimer.current && clearTimeout(reloadTimer.current);
  }, [elements, scriptId, reload]);

  const generate = useCallback(async (sceneId) => {
    setBusy((b) => ({ ...b, [sceneId]: true }));
    try {
      const { data } = await api.post(`/scripts/${scriptId}/outline/summaries/${sceneId}/generate`);
      setSummaries((m) => ({ ...m, [sceneId]: { ...(m[sceneId] || {}), summary: data.summary, has_summary: true, stale: false } }));
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't summarize that scene");
      return false;
    } finally {
      setBusy((b) => ({ ...b, [sceneId]: false }));
    }
  }, [scriptId]);

  const targets = outline.filter((o) => { const s = summaries[o.id]; return !s?.has_summary || s?.stale; });

  const summarizeAll = async () => {
    setBulk(true);
    for (const o of targets) {
      // eslint-disable-next-line no-await-in-loop
      await generate(o.id);
    }
    setBulk(false);
  };

  if (outline.length === 0) {
    return <Empty text="Scene headings appear here as you write." />;
  }

  return (
    <div className="p-2" data-testid="outline-list">
      {canGenerate && targets.length > 0 && (
        <div className="flex items-center justify-end mb-1 px-1">
          <button
            onClick={summarizeAll}
            disabled={bulk}
            className="text-[10px] uppercase tracking-wider px-2 py-1 rounded text-[var(--ink-muted)] hover:bg-[var(--warm-accent)] flex items-center gap-1 disabled:opacity-50"
            data-testid="outline-summarize-all-btn"
          >
            <RefreshCw size={11} className={bulk ? "animate-spin" : ""} />
            {bulk ? "Summarizing…" : `Summarize all (${targets.length})`}
          </button>
        </div>
      )}
      <ul className="list-none m-0 p-0">
        {outline.map((item, i) => {
          const s = summaries[item.id] || {};
          const isBusy = !!busy[item.id];
          return (
            <li key={item.id} className="rounded group mb-0.5" data-testid={`outline-item-${item.index}`}>
              <button
                onClick={() => jumpToScene && jumpToScene(i)}
                className="w-full text-left px-2.5 pt-2 pb-1 hover:bg-[var(--warm-accent)] rounded-t text-sm"
                data-testid={`outline-jump-${item.index}`}
              >
                <span className="text-[var(--ink-muted)] mr-2 font-mono-script text-xs">{item.number}.</span>
                <span className="text-[var(--ink-primary)] uppercase tracking-wide text-[12px]">{item.text || "(untitled scene)"}</span>
              </button>
              <div className="px-2.5 pb-2 pl-7">
                {s.has_summary ? (
                  <>
                    <p className="text-[12px] text-[var(--ink-secondary)] leading-relaxed" data-testid={`outline-summary-${item.index}`}>{s.summary}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {s.stale && (
                        <span className="text-[9px] uppercase tracking-wider text-[var(--warn)] bg-[var(--warn)]/15 px-1.5 py-0.5 rounded" data-testid={`outline-stale-${item.index}`}>Outdated</span>
                      )}
                      {canGenerate && (
                        <button
                          onClick={() => generate(item.id)}
                          disabled={isBusy}
                          className={`text-[10px] uppercase tracking-wider flex items-center gap-1 hover:underline disabled:opacity-50 ${s.stale ? "text-[var(--brand)]" : "text-[var(--ink-muted)] opacity-0 group-hover:opacity-100"}`}
                          data-testid={`outline-refresh-${item.index}`}
                        >
                          <RefreshCw size={10} className={isBusy ? "animate-spin" : ""} /> {isBusy ? "Summarizing…" : (s.stale ? "Refresh" : "Redo")}
                        </button>
                      )}
                    </div>
                  </>
                ) : canGenerate ? (
                  <button
                    onClick={() => generate(item.id)}
                    disabled={isBusy}
                    className="text-[11px] text-[var(--brand)] hover:underline flex items-center gap-1 disabled:opacity-50"
                    data-testid={`outline-generate-${item.index}`}
                  >
                    <RefreshCw size={10} className={isBusy ? "animate-spin" : ""} /> {isBusy ? "Summarizing…" : "Summarize this scene"}
                  </button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ============================================================================
// Notes
// ============================================================================
function NotesPanel({ scriptId }) {
  const confirm = useConfirm();
  const openImage = useLightbox();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", scope: "script" });
  const [pendingImages, setPendingImages] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [expandedMap, setExpandedMap] = useState({});
  const reorderTimer = useRef(null);
  const createFileRef = useRef(null);

  const reload = useCallback(async () => {
    try { const { data } = await api.get(`/scripts/${scriptId}/notes`); setItems(data.notes || []); } catch { /* ignore */ }
  }, [scriptId]);
  useEffect(() => {
    if (!scriptId) return undefined;
    let alive = true;
    api.get(`/scripts/${scriptId}/notes`)
      .then(({ data }) => { if (alive) setItems(data.notes || []); })
      .catch(() => { /* ignore */ });
    return () => { alive = false; };
  }, [scriptId]);

  const addPendingImage = async (file) => {
    if (!file) return;
    if (file.size > 4_500_000) { toast.error("Image must be under ~4MB"); return; }
    try { const dataUrl = await fileToDataUrl(file); setPendingImages((p) => [...p, dataUrl]); } catch { /* ignore */ }
  };

  const addPendingAny = (file) => {
    if (!file) return;
    if (file.type?.startsWith("image/")) { addPendingImage(file); return; }
    if (file.size > 15_000_000) { toast.error("File must be under 15MB"); return; }
    setPendingFiles((p) => [...p, file]);
  };

  const create = async (e) => {
    e?.preventDefault();
    if (!form.title.trim()) return;
    try {
      const { data } = await api.post(`/scripts/${scriptId}/notes`, form);
      const noteId = data.note?.id;
      if (noteId && pendingImages.length) {
        for (const dataUrl of pendingImages) {
          try { await api.post(`/scripts/${scriptId}/notes/${noteId}/images`, { data_url: dataUrl }); } catch { /* ignore */ }
        }
      }
      if (noteId && pendingFiles.length) {
        for (const f of pendingFiles) {
          try {
            const fd = new FormData();
            fd.append("file", f, f.name);
            await api.post(`/scripts/${scriptId}/notes/${noteId}/files`, fd);
          } catch { toast.error(`Couldn't attach ${f.name}`); }
        }
      }
      setForm({ title: "", body: "", scope: "script" });
      setPendingImages([]);
      setPendingFiles([]);
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not add note");
    }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: "Delete this note?",
      description: "This note, its images and attached files will be removed.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!ok) return;
    try { await api.delete(`/scripts/${scriptId}/notes/${id}`); setItems((prev) => prev.filter((n) => n.id !== id)); } catch { /* ignore */ }
  };

  const uploadImage = async (id, file) => {
    if (!file) return;
    if (file.size > 4_500_000) { toast.error("Image must be under ~4MB"); return; }
    try {
      const dataUrl = await fileToDataUrl(file);
      const { data } = await api.post(`/scripts/${scriptId}/notes/${id}/images`, { data_url: dataUrl });
      setItems((prev) => prev.map((n) => (n.id === id ? data.note : n)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    }
  };

  const removeImage = async (id, idx) => {
    const ok = await confirm({
      title: "Delete this image?",
      description: "This image will be removed from the note.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!ok) return;
    try {
      const { data } = await api.delete(`/scripts/${scriptId}/notes/${id}/images/${idx}`);
      setItems((prev) => prev.map((n) => (n.id === id ? data.note : n)));
    } catch { toast.error("Could not remove image"); }
  };

  const uploadFile = async (id, file) => {
    if (!file) return;
    if (file.size > 15_000_000) { toast.error("File must be under 15MB"); return; }
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      const { data } = await api.post(`/scripts/${scriptId}/notes/${id}/files`, fd);
      setItems((prev) => prev.map((n) => (n.id === id ? data.note : n)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    }
  };

  const removeFile = async (id, fileId) => {
    const ok = await confirm({
      title: "Delete this file?",
      description: "This file will be removed from the note.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!ok) return;
    try {
      const { data } = await api.delete(`/scripts/${scriptId}/notes/${id}/files/${fileId}`);
      setItems((prev) => prev.map((n) => (n.id === id ? data.note : n)));
    } catch { toast.error("Could not remove file"); }
  };

  const downloadFile = async (id, meta) => {
    try {
      const { data } = await api.get(`/scripts/${scriptId}/notes/${id}/files/${meta.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = meta.name || "file";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the file"); }
  };

  const updateNote = async (id, patch) => {
    const current = items.find((n) => n.id === id) || {};
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, ...patch } : n)));
    const body = {
      title: patch.title ?? current.title ?? "",
      body: patch.body ?? current.body ?? "",
      scope: current.scope || "script",
      scope_ref: current.scope_ref ?? null,
    };
    try { await api.put(`/scripts/${scriptId}/notes/${id}`, body); }
    catch { toast.error("Could not save note"); }
  };

  const persistOrder = (next) => {
    setItems(next);
    if (reorderTimer.current) clearTimeout(reorderTimer.current);
    reorderTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.put(`/scripts/${scriptId}/notes/reorder`, {
          items: next.map((n) => ({ id: n.id, pinned: !!n.pinned })),
        });
        if (data.notes) setItems(data.notes);
      } catch { toast.error("Could not save the new order"); }
    }, 600);
  };

  const pinned = items.filter((n) => n.pinned);
  const unpinned = items.filter((n) => !n.pinned);

  const togglePin = (id) => {
    const updated = items.map((n) => (n.id === id ? { ...n, pinned: !n.pinned } : n));
    const next = [...updated.filter((n) => n.pinned), ...updated.filter((n) => !n.pinned)];
    persistOrder(next);
  };
  const onReorderPinned = (newP) => persistOrder([...newP, ...unpinned]);
  const onReorderUnpinned = (newU) => persistOrder([...pinned, ...newU]);

  const allExpanded = items.length > 0 && items.every((n) => expandedMap[n.id]);
  const toggleExpandAll = () => {
    if (allExpanded) { setExpandedMap({}); return; }
    const m = {};
    items.forEach((n) => { m[n.id] = true; });
    setExpandedMap(m);
  };
  const setExpanded = (id, val) => setExpandedMap((m) => ({ ...m, [id]: val }));

  const renderCard = (n) => (
    <NoteCard
      key={n.id}
      note={n}
      expanded={!!expandedMap[n.id]}
      onToggleExpand={() => setExpanded(n.id, !expandedMap[n.id])}
      onTogglePin={() => togglePin(n.id)}
      onSave={(patch) => updateNote(n.id, patch)}
      onRemove={() => remove(n.id)}
      onUploadImage={(file) => uploadImage(n.id, file)}
      onRemoveImage={(idx) => removeImage(n.id, idx)}
      onUploadFile={(file) => uploadFile(n.id, file)}
      onRemoveFile={(fileId) => removeFile(n.id, fileId)}
      onDownloadFile={(meta) => downloadFile(n.id, meta)}
      onOpenImage={openImage}
    />
  );

  return (
    <div className="p-3" data-testid="notes-panel">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs uppercase tracking-wider rounded border border-dashed border-[var(--line-strong)] text-[var(--ink-secondary)] hover:bg-[var(--warm-accent)] mb-3" data-testid="add-note-btn">
        <Plus size={14} strokeWidth={1.5} /> New note
      </button>
      {open && (
        <form onSubmit={create} className="bg-white border border-[var(--line)] rounded p-3 mb-3 space-y-2" data-testid="note-form">
          <input data-testid="note-title-input" autoFocus placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full text-sm bg-transparent border-b border-[var(--line)] focus:border-[var(--brand)] outline-none py-1.5" />
          <textarea data-testid="note-body-input" placeholder="Body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="w-full text-sm bg-transparent border-b border-[var(--line)] focus:border-[var(--brand)] outline-none py-1.5 min-h-[80px] resize-none" />
          <div className="flex flex-wrap gap-2" data-testid="note-create-images">
            {pendingImages.map((url, i) => (
              <div key={i} className="relative group/img">
                <img src={url} alt="" onClick={() => openImage(url)} className="w-12 h-12 object-cover rounded border border-[var(--line)] cursor-zoom-in" data-testid={`note-create-image-${i}`} />
                <button
                  type="button"
                  onClick={() => setPendingImages((p) => p.filter((_, idx) => idx !== i))}
                  className="absolute -top-1 -right-1 bg-white border border-[var(--line)] rounded-full p-0.5 opacity-0 group-hover/img:opacity-100"
                  data-testid={`note-create-image-remove-${i}`}
                ><X size={10} strokeWidth={2} className="text-[var(--err)]" /></button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => createFileRef.current?.click()}
              className="w-12 h-12 rounded border border-dashed border-[var(--line-strong)] flex items-center justify-center text-[var(--ink-muted)] hover:bg-[var(--warm-accent)]"
              data-testid="note-create-image-upload"
              title="Attach image or file"
            ><Upload size={13} strokeWidth={1.5} /></button>
            <input
              ref={createFileRef}
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                files.forEach((f) => addPendingAny(f));
                if (createFileRef.current) createFileRef.current.value = "";
              }}
              data-testid="note-create-image-input"
            />
          </div>
          {pendingFiles.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="note-create-files">
              {pendingFiles.map((f, i) => (
                <FileChip
                  key={`${f.name}-${i}`}
                  name={f.name}
                  type={f.type}
                  size={f.size}
                  testId={`note-create-file-${i}`}
                  onRemove={() => setPendingFiles((p) => p.filter((_, idx) => idx !== i))}
                  removeTestId={`note-create-file-remove-${i}`}
                />
              ))}
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <button type="submit" className="btn-brand text-xs px-3 py-1.5" data-testid="note-save-btn">Save</button>
            <button type="button" onClick={() => { setOpen(false); setPendingImages([]); setPendingFiles([]); }} className="btn-ghost text-xs px-3 py-1.5" data-testid="note-cancel-btn">Cancel</button>
          </div>
        </form>
      )}

      {items.length > 0 && (
        <div className="flex items-center justify-end mb-2">
          <button onClick={toggleExpandAll} className="text-[10px] uppercase tracking-wider px-2 py-1 rounded text-[var(--ink-muted)] hover:bg-[var(--warm-accent)] flex items-center gap-1" data-testid="notes-expand-all-btn">
            {allExpanded ? <ChevronsDownUp size={12} /> : <ChevronsUpDown size={12} />}
            {allExpanded ? "Collapse all" : "Expand all"}
          </button>
        </div>
      )}

      {items.length === 0 && !open && <Empty text="No notes yet." />}

      {pinned.length > 0 && (
        <div className="mb-3" data-testid="notes-pinned-section">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-[var(--ink-muted)] mb-1.5 px-0.5">
            <Pin size={11} className="fill-current" /> Pinned
          </div>
          <Reorder.Group axis="y" values={pinned} onReorder={onReorderPinned} className="space-y-2 list-none m-0 p-0" as="div">
            {pinned.map(renderCard)}
          </Reorder.Group>
        </div>
      )}

      <Reorder.Group axis="y" values={unpinned} onReorder={onReorderUnpinned} className="space-y-2 list-none m-0 p-0" as="div">
        {unpinned.map(renderCard)}
      </Reorder.Group>
    </div>
  );
}

function NoteCard({ note, expanded, onToggleExpand, onTogglePin, onSave, onRemove, onUploadImage, onRemoveImage, onUploadFile, onRemoveFile, onDownloadFile, onOpenImage }) {
  const controls = useDragControls();
  const fileInputRef = useRef(null);
  // De-dupe the duplicate click event (same timeStamp) that can fire on both the
  // <img> and its overlaid delete button — only the first (real target) wins.
  const clickClaimRef = useRef(-1);
  const claimClick = (e) => {
    if (e.timeStamp === clickClaimRef.current) return false;
    clickClaimRef.current = e.timeStamp;
    return true;
  };
  const [title, setTitle] = useState(note.title || "");
  const images = note.images || [];
  const files = note.files || [];

  return (
    <Reorder.Item
      value={note}
      dragListener={false}
      dragControls={controls}
      className="bg-white border border-[var(--line)] rounded group list-none"
      data-testid={`note-card-${note.id}`}
    >
      <div className="flex items-center gap-1 p-2">
        <button
          onPointerDown={(e) => controls.start(e)}
          className="p-1 text-[var(--ink-muted)] hover:text-[var(--ink-primary)] cursor-grab active:cursor-grabbing touch-none shrink-0"
          data-testid={`note-drag-${note.id}`}
          title="Drag to reorder"
        >
          <GripVertical size={14} />
        </button>
        <button
          onClick={onToggleExpand}
          className="p-1 text-[var(--ink-muted)] hover:text-[var(--ink-primary)] shrink-0"
          data-testid={`note-expand-${note.id}`}
          aria-expanded={expanded}
          title={expanded ? "Collapse" : "Expand"}
        >
          {expanded ? <ChevronDown size={14} strokeWidth={2} /> : <ChevronRight size={14} strokeWidth={2} />}
        </button>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => { if ((title || "") !== (note.title || "")) onSave({ title }); }}
          placeholder="Note title"
          data-testid={`note-title-${note.id}`}
          className="flex-1 min-w-0 text-sm font-semibold text-[var(--ink-primary)] bg-transparent border-b border-transparent focus:border-[var(--brand)] outline-none py-0.5"
        />
        <button
          onClick={onTogglePin}
          className={`shrink-0 p-1 hover:text-[var(--brand)] ${note.pinned ? "text-[var(--brand)]" : "text-[var(--ink-muted)] opacity-0 group-hover:opacity-100"}`}
          data-testid={`note-pin-${note.id}`}
          title={note.pinned ? "Unpin" : "Pin to top"}
        >
          <Pin size={13} className={note.pinned ? "fill-current" : ""} />
        </button>
        <button onClick={onRemove} className="opacity-0 group-hover:opacity-100 text-[var(--ink-muted)] hover:text-[var(--err)] shrink-0 p-1" data-testid={`note-delete-${note.id}`}><Trash2 size={14} /></button>
      </div>

      {expanded && (
        <div className="pb-3" data-testid={`note-body-section-${note.id}`}>
          <RichTextField
            html={note.body}
            onCommit={(v) => { if (v !== (note.body || "")) onSave({ body: v }); }}
            placeholder="Write your note…"
            testId={`note-body-${note.id}`}
          />
          <div className="flex flex-wrap gap-2 mt-2.5 px-3" data-testid={`note-images-${note.id}`}>
            {images.map((url, i) => (
              <div key={i} className="relative group/img">
                <img src={url} alt={`${note.title} ${i + 1}`} onClick={(e) => { if (claimClick(e)) onOpenImage(url); }} className="w-12 h-12 object-cover rounded border border-[var(--line)] cursor-zoom-in" data-testid={`note-image-${note.id}-${i}`} />
                <button
                  onClick={(e) => { e.stopPropagation(); if (claimClick(e)) onRemoveImage(i); }}
                  className="absolute -top-1 -right-1 bg-white border border-[var(--line)] rounded-full p-0.5 opacity-0 group-hover/img:opacity-100"
                  data-testid={`note-image-remove-${note.id}-${i}`}
                ><X size={10} strokeWidth={2} className="text-[var(--err)]" /></button>
              </div>
            ))}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-12 h-12 rounded border border-dashed border-[var(--line-strong)] flex items-center justify-center text-[var(--ink-muted)] hover:bg-[var(--warm-accent)]"
              data-testid={`note-image-upload-${note.id}`}
              title="Attach image or file"
            ><Upload size={13} strokeWidth={1.5} /></button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={(e) => {
                const fl = Array.from(e.target.files || []);
                fl.forEach((f) => (f.type?.startsWith("image/") ? onUploadImage(f) : onUploadFile(f)));
                if (fileInputRef.current) fileInputRef.current.value = "";
              }}
              data-testid={`note-image-input-${note.id}`}
            />
          </div>
          {files.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2 px-3" data-testid={`note-files-${note.id}`}>
              {files.map((f) => (
                <FileChip
                  key={f.id}
                  name={f.name}
                  type={f.content_type}
                  size={f.size}
                  testId={`note-file-${note.id}-${f.id}`}
                  onOpen={() => onDownloadFile(f)}
                  onRemove={() => onRemoveFile(f.id)}
                  removeTestId={`note-file-remove-${note.id}-${f.id}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </Reorder.Item>
  );
}

// ============================================================================
// Beats
// ============================================================================
function BeatsPanel({ scriptId }) {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", summary: "" });
  const reorderTimer = useRef(null);
  const [expandedMap, setExpandedMap] = useState({});

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/scripts/${scriptId}/beats`); setItems(data.beats || []); } catch { /* ignore */ }
  }, [scriptId]);
  useEffect(() => {
    if (!scriptId) return undefined;
    let alive = true;
    api.get(`/scripts/${scriptId}/beats`)
      .then(({ data }) => { if (alive) setItems(data.beats || []); })
      .catch(() => { /* ignore */ });
    return () => { alive = false; };
  }, [scriptId]);

  const create = async (e) => {
    e?.preventDefault();
    if (!form.title.trim()) return;
    try {
      await api.post(`/scripts/${scriptId}/beats`, { ...form, position: items.length });
      setForm({ title: "", summary: "" });
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not add beat");
    }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: "Delete this beat?",
      description: "This beat card will be removed from your outline.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!ok) return;
    try { await api.delete(`/scripts/${scriptId}/beats/${id}`); setItems((prev) => prev.filter((b) => b.id !== id)); } catch { /* ignore */ }
  };

  const saveBeat = async (id, patch) => {
    const current = items.find((b) => b.id === id) || {};
    setItems((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
    const body = {
      title: patch.title ?? current.title ?? "",
      summary: patch.summary ?? current.summary ?? "",
      position: current.position ?? 0,
      linked_scene_index: current.linked_scene_index ?? null,
    };
    try { await api.put(`/scripts/${scriptId}/beats/${id}`, body); }
    catch { toast.error("Could not save beat"); }
  };

  const handleReorder = (newOrder) => {
    setItems(newOrder);
    if (reorderTimer.current) clearTimeout(reorderTimer.current);
    reorderTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.put(`/scripts/${scriptId}/beats/reorder`, { ordered_ids: newOrder.map((b) => b.id) });
        if (data.beats) setItems(data.beats);
      } catch { toast.error("Could not save the new order"); }
    }, 600);
  };

  const allExpanded = items.length > 0 && items.every((b) => expandedMap[b.id]);
  const toggleExpandAll = () => {
    if (allExpanded) { setExpandedMap({}); return; }
    const m = {};
    items.forEach((b) => { m[b.id] = true; });
    setExpandedMap(m);
  };
  const setExpanded = (id, val) => setExpandedMap((m) => ({ ...m, [id]: val }));

  return (
    <div className="p-3" data-testid="beats-panel">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs uppercase tracking-wider rounded border border-dashed border-[var(--line-strong)] text-[var(--ink-secondary)] hover:bg-[var(--warm-accent)] mb-3" data-testid="add-beat-btn">
        <Plus size={14} strokeWidth={1.5} /> New beat
      </button>
      {open && (
        <form onSubmit={create} className="bg-white border border-[var(--line)] rounded p-3 mb-3 space-y-2" data-testid="beat-form">
          <input data-testid="beat-title-input" autoFocus placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full text-sm bg-transparent border-b border-[var(--line)] focus:border-[var(--brand)] outline-none py-1.5" />
          <textarea data-testid="beat-summary-input" placeholder="Summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="w-full text-sm bg-transparent border-b border-[var(--line)] focus:border-[var(--brand)] outline-none py-1.5 min-h-[60px] resize-none" />
          <div className="flex gap-2 pt-1">
            <button type="submit" className="btn-brand text-xs px-3 py-1.5" data-testid="beat-save-btn">Save</button>
            <button type="button" onClick={() => setOpen(false)} className="btn-ghost text-xs px-3 py-1.5" data-testid="beat-cancel-btn">Cancel</button>
          </div>
        </form>
      )}
      {items.length > 0 && (
        <div className="flex items-center justify-end mb-2">
          <button onClick={toggleExpandAll} className="text-[10px] uppercase tracking-wider px-2 py-1 rounded text-[var(--ink-muted)] hover:bg-[var(--warm-accent)] flex items-center gap-1" data-testid="beats-expand-all-btn">
            {allExpanded ? <ChevronsDownUp size={12} /> : <ChevronsUpDown size={12} />}
            {allExpanded ? "Collapse all" : "Expand all"}
          </button>
        </div>
      )}
      {items.length === 0 && !open ? (
        <Empty text="Plot beats appear here. Drag to reorder." />
      ) : (
        <Reorder.Group axis="y" values={items} onReorder={handleReorder} className="space-y-2 list-none m-0 p-0" as="ol">
          {items.map((b, idx) => (
            <BeatCard
              key={b.id}
              beat={b}
              index={idx}
              expanded={!!expandedMap[b.id]}
              onToggleExpand={() => setExpanded(b.id, !expandedMap[b.id])}
              onSave={(patch) => saveBeat(b.id, patch)}
              onRemove={() => remove(b.id)}
            />
          ))}
        </Reorder.Group>
      )}
    </div>
  );
}

function BeatCard({ beat, index, expanded, onToggleExpand, onSave, onRemove }) {
  const controls = useDragControls();
  const [title, setTitle] = useState(beat.title || "");

  const headerRow = (padBottom) => (
    <div className={`flex items-start gap-1.5 px-3 pt-3 ${padBottom}`}>
      <button
        onPointerDown={(e) => controls.start(e)}
        className="mt-1 text-[var(--ink-muted)] hover:text-[var(--ink-primary)] cursor-grab active:cursor-grabbing touch-none shrink-0"
        data-testid={`beat-drag-${beat.id}`}
        title="Drag to reorder"
      >
        <GripVertical size={14} />
      </button>
      <span className="text-[var(--ink-muted)] font-mono-script text-sm mt-1 shrink-0">{index + 1}.</span>
      <button
        onClick={onToggleExpand}
        className="mt-1 text-[var(--ink-muted)] hover:text-[var(--ink-primary)] shrink-0"
        data-testid={`beat-expand-${beat.id}`}
        aria-expanded={expanded}
        title={expanded ? "Collapse" : "Expand"}
      >
        {expanded ? <ChevronDown size={14} strokeWidth={2} /> : <ChevronRight size={14} strokeWidth={2} />}
      </button>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onBlur={() => { if ((title || "") !== (beat.title || "")) onSave({ title }); }}
        placeholder="Beat title"
        data-testid={`beat-title-${beat.id}`}
        className="flex-1 min-w-0 text-sm font-semibold text-[var(--ink-primary)] bg-transparent border-b border-transparent focus:border-[var(--brand)] outline-none py-0.5"
      />
      <button onClick={onRemove} className="opacity-0 group-hover:opacity-100 text-[var(--ink-muted)] hover:text-[var(--err)] shrink-0 mt-0.5" data-testid={`beat-delete-${beat.id}`}>
        <Trash2 size={14} />
      </button>
    </div>
  );

  return (
    <Reorder.Item
      value={beat}
      dragListener={false}
      dragControls={controls}
      className="bg-white border border-[var(--line)] rounded group list-none"
      data-testid={`beat-card-${beat.id}`}
    >
      {expanded ? (
        <RichTextField
          html={beat.summary}
          onCommit={(v) => { if (v !== (beat.summary || "")) onSave({ summary: v }); }}
          placeholder="What happens in this beat…"
          testId={`beat-summary-${beat.id}`}
          header={headerRow("pb-1.5")}
        />
      ) : (
        headerRow("pb-3")
      )}
    </Reorder.Item>
  );
}

function FileChip({ name, type, size, onOpen, onRemove, testId, removeTestId }) {
  const Icon = fileTypeIcon(type);
  return (
    <div className="relative group/file inline-flex max-w-full">
      <button
        type="button"
        onClick={onOpen}
        className={`inline-flex items-center gap-1.5 max-w-[200px] pl-2 pr-3 py-1 rounded-md border border-[var(--line)] bg-white text-[11px] text-[var(--ink-secondary)] ${onOpen ? "hover:bg-[var(--warm-accent)] hover:text-[var(--ink-primary)] cursor-pointer" : "cursor-default"} transition-colors`}
        data-testid={testId}
        title={onOpen ? `${name} — click to download` : name}
      >
        <Icon size={12} strokeWidth={1.5} className="shrink-0 text-[var(--brand)]" />
        <span className="truncate">{name}</span>
        <span className="text-[10px] text-[var(--ink-muted)] shrink-0">{fmtSize(size)}</span>
      </button>
      {onRemove && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="absolute -top-1 -right-1 bg-white border border-[var(--line)] rounded-full p-0.5 opacity-0 group-hover/file:opacity-100"
          data-testid={removeTestId}
        ><X size={10} strokeWidth={2} className="text-[var(--err)]" /></button>
      )}
    </div>
  );
}

function fileTypeIcon(type = "") {
  const t = type || "";
  if (t.startsWith("audio/")) return FileAudio;
  if (t.startsWith("video/")) return FileVideo;
  if (t.includes("pdf") || t.startsWith("text/") || t.includes("document") || t.includes("word")) return FileTextIcon;
  return FileIcon;
}

function fmtSize(bytes) {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1_048_576).toFixed(1)} MB`;
}

function Empty({ text }) {
  return <p className="text-xs text-[var(--ink-muted)] italic px-4 py-6 text-center" data-testid="empty-state">{text}</p>;
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/* ---- Rich text (beat summary) ------------------------------------------- */

const RT_ALLOWED = new Set(["B", "STRONG", "I", "EM", "U", "H3", "UL", "OL", "LI", "BR", "DIV", "P", "SPAN"]);
const RT_DROP = new Set(["SCRIPT", "STYLE", "IFRAME", "OBJECT", "EMBED", "LINK", "META"]);

function escapeHtml(str) {
  return str.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
}

/** Whitelist tags + strip all attributes (XSS-safe — beats live in shared scripts). */
function sanitizeHtml(html) {
  const tmp = document.createElement("div");
  tmp.innerHTML = html || "";
  const clean = (node) => {
    [...node.childNodes].forEach((child) => {
      if (child.nodeType === 1) {
        if (RT_DROP.has(child.tagName)) { child.remove(); return; }
        clean(child);
        if (!RT_ALLOWED.has(child.tagName)) {
          child.replaceWith(...child.childNodes);
        } else {
          [...child.attributes].forEach((a) => child.removeAttribute(a.name));
        }
      } else if (child.nodeType === 8) {
        child.remove();
      }
    });
  };
  clean(tmp);
  return tmp.innerHTML;
}

/** Coerce a stored value (rich HTML or legacy plain text) into safe display HTML. */
function toDisplayHtml(value) {
  const v = value || "";
  if (v.includes("<")) return sanitizeHtml(v);
  return escapeHtml(v).replace(/\n/g, "<br>");
}

const RT_TOOLS = [
  { cmd: "bold", icon: Bold, label: "Bold" },
  { cmd: "italic", icon: Italic, label: "Italic" },
  { cmd: "formatBlock", arg: "H3", icon: Heading3, label: "Heading" },
  { cmd: "insertUnorderedList", icon: List, label: "Bullet list" },
  { cmd: "insertOrderedList", icon: ListOrdered, label: "Numbered list" },
];

/**
 * Lightweight rich-text field (contentEditable + execCommand) with a small
 * Bold / Italic / Heading / List toolbar. Uncontrolled: initial value set via
 * innerHTML, changes committed (sanitized) on blur. The `header` slot + toolbar
 * sit in a sticky zone so they stay pinned to the top while the body scrolls.
 */
function RichTextField({ html, onCommit, placeholder, testId, header }) {
  const ref = useRef(null);
  const focusedRef = useRef(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || focusedRef.current) return;
    const next = toDisplayHtml(html);
    if (el.innerHTML !== next) el.innerHTML = next;
  }, [html]);

  const exec = (cmd, arg) => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    document.execCommand(cmd, false, arg);
  };

  const handleBlur = () => {
    focusedRef.current = false;
    const el = ref.current;
    if (!el) return;
    const text = (el.textContent || "").trim();
    const clean = text ? sanitizeHtml(el.innerHTML) : "";
    if (clean !== (html || "")) onCommit(clean);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  return (
    <div data-testid={`${testId}-wrap`}>
      <div className="sticky top-0 z-10 bg-white rounded-t border-b border-[var(--line)]">
        {header}
        <div className="flex items-center gap-0.5 px-2 pb-1.5" data-testid={`${testId}-toolbar`}>
          {RT_TOOLS.map((t) => (
            <button
              key={t.label}
              type="button"
              title={t.label}
              // onMouseDown + preventDefault keeps the editor focused/selection intact
              onMouseDown={(e) => { e.preventDefault(); exec(t.cmd, t.arg); }}
              className="p-1 rounded text-[var(--ink-muted)] hover:bg-[var(--warm-accent)] hover:text-[var(--ink-primary)]"
              data-testid={`${testId}-${t.cmd}`}
            >
              <t.icon size={13} strokeWidth={2} />
            </button>
          ))}
        </div>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        onFocus={() => { focusedRef.current = true; }}
        onBlur={handleBlur}
        onPaste={handlePaste}
        data-placeholder={placeholder}
        data-testid={testId}
        className="rich-text-field w-full text-xs text-[var(--ink-secondary)] outline-none pl-3 pr-2 py-2 min-h-[60px] leading-relaxed"
      />
    </div>
  );
}
